import React, { useState, useEffect, useCallback } from "react";
import "./MainCalendarV2.css";
import { getCalendar, getUsersSummary, getAllDaysOff } from "../../../api/api";
import { getHrInspectorReports } from "../../../api/reportsApi";
import BirthdayList from "./components/BirthdayList/BirthdayList";
import EventsList from "./components/EventsList/EventsList";
import VacationList from "./components/VacationList/VacationList";
import { ReactComponent as CakeIcon } from "../../../images/calendar/cake.svg";
import { ReactComponent as HolidayIcon } from "../../../images/calendar/holiday.svg";
import { ReactComponent as CorporateIcon } from "../../../images/calendar/corporate.svg";
import { ReactComponent as DayoffsIcon } from "../../../images/calendar/dayoffs.svg";
import CustomTooltip from "./CustomTooltip";
import ru from "date-fns/locale/ru";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

const EVENT_TYPES = [
  { id: "holidays", label: "Праздники и мероприятия", color: "#FFBDBD" },
  { id: "corporate", label: "Корпоративные мероприятия", color: "#C7F5C7" },
  { id: "absences", label: "Отсутствие сотрудников", color: "#E6E6E6" },
  { id: "birthdays", label: "Дни рождения", color: "#BDE0FF" },
];

const months = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const formatDateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ICONS = {
  holidays: <HolidayIcon style={{ color: "#FF5A5A", width: 18, height: 18 }} />,
  corporate: (
    <CorporateIcon style={{ color: "#2A5E38", width: 18, height: 18 }} />
  ),
  absences: <DayoffsIcon style={{ color: "#FFB86C", width: 18, height: 18 }} />,
  birthdays: <CakeIcon style={{ color: "#3A8DFF", width: 18, height: 18 }} />,
};

const ICON_BG = {
  holidays: "#FFBDBD",
  corporate: "#C7F5C7",
  absences: "#FFE3BD",
  birthdays: "#BDE0FF",
};

function shortName(fullName) {
  if (!fullName) return "";
  const [surname, name] = fullName.split(" ");
  return surname && name ? `${surname} ${name[0]}.` : fullName;
}

const absenceColors = {
  Отпуск: "#E6F4EA",
  Больничный: "#FFE6E6",
  Отгул: "#E6F0FF",
  "Отпуск без содержания": "#FFF3E0",
  "Учебный отпуск": "#E0F2F1",
  Командировка: "#F0E6FF",
};

const getWeekRange = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Пн
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday, end: sunday };
};

const MainCalendarV2 = () => {
  const [enabledTypes, setEnabledTypes] = useState({
    holidays: true,
    corporate: true,
    absences: true,
    birthdays: true,
  });
  const [calendarData, setCalendarData] = useState({});
  const [vacationData, setVacationData] = useState({});
  const [birthdayData, setBirthdayData] = useState({});
  const [daysOffData, setDaysOffData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [selectedView, setSelectedView] = useState("month");
  const [yearCalendarData, setYearCalendarData] = useState([]);
  const [yearLoading, setYearLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const navigate = useNavigate();

  // Загрузка календарных событий (праздники, мероприятия)
  const loadCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const prevMonthFirstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const nextMonthLastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 2,
        0
      );
      const dateFrom = formatDateToString(prevMonthFirstDay);
      const dateTo = formatDateToString(nextMonthLastDay);
      const data = await getCalendar(dateFrom, dateTo);
      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });
      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  // Загрузка отпусков и больничных
  const loadVacationData = useCallback(async () => {
    try {
      let page = 1;
      const pageSize = 100;
      let allReports = [];
      let hasMore = true;

      while (hasMore) {
        const response = await getHrInspectorReports({
          page,
          page_size: pageSize,
        });
        allReports = allReports.concat(response.reports);
        if (!response.reports || response.reports.length < pageSize) {
          hasMore = false;
        } else {
          page += 1;
        }
      }

      // Группируем по user+type
      const grouped = {};
      allReports.forEach((report) => {
        const key = `${report.user}__${report.portal_job_type_name}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(report);
      });
      // Для каждой группы разбиваем на непрерывные периоды
      const vacationMap = {};
      Object.values(grouped).forEach((reports) => {
        // Сортируем даты
        const sortedDates = reports
          .map((r) => new Date(r.fact_date_start))
          .sort((a, b) => a - b);
        // Разбиваем на интервалы
        let periods = [];
        let currentPeriod = [];
        for (let i = 0; i < sortedDates.length; i++) {
          if (currentPeriod.length === 0) {
            currentPeriod.push(sortedDates[i]);
          } else {
            const prev = currentPeriod[currentPeriod.length - 1];
            const curr = sortedDates[i];
            const diff = (curr - prev) / (1000 * 60 * 60 * 24);
            if (diff === 1) {
              currentPeriod.push(curr);
            } else {
              periods.push([...currentPeriod]);
              currentPeriod = [curr];
            }
          }
        }
        if (currentPeriod.length > 0) {
          periods.push([...currentPeriod]);
        }
        // Для каждого периода создаём записи
        periods.forEach((period) => {
          const minDate = period[0];
          const maxDate = period[period.length - 1];
          period.forEach((date) => {
            const dateString = formatDateToString(date);
            if (!vacationMap[dateString]) vacationMap[dateString] = [];
            // Берём первый report для user/type/department
            const report = reports.find(
              (r) => new Date(r.fact_date_start).getTime() === date.getTime()
            );
            vacationMap[dateString].push({
              user: report.user,
              type: report.portal_job_type_name,
              department: report.group_name,
              period_start: minDate.toISOString(),
              period_end: maxDate.toISOString(),
              project: report.project_name,
            });
          });
        });
      });
      setVacationData(vacationMap);
    } catch (error) {
      console.error("Ошибка при загрузке отпусков:", error);
    }
  }, []);

  // Загрузка дней рождений
  const loadBirthdayData = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const response = await getUsersSummary({
        isActive: true,
        pageSize: 1000,
      });
      const birthdayMap = {};
      response.users.forEach((user) => {
        if (!user.birth_date) return;
        const birthDate = new Date(user.birth_date);
        const birthMonth = birthDate.getMonth();
        const birthDay = birthDate.getDate();
        if (birthMonth === month) {
          const dateString = formatDateToString(
            new Date(year, month, birthDay)
          );
          if (!birthdayMap[dateString]) birthdayMap[dateString] = [];
          const age = year - birthDate.getFullYear();
          const isJubilee = age % 5 === 0;
          birthdayMap[dateString].push({
            user: `${user.surname} ${user.name} ${user.patronymic}`,
            age,
            isJubilee,
          });
        }
      });
      setBirthdayData(birthdayMap);
    } catch (error) {
      console.error("Ошибка при загрузке дней рождений:", error);
    }
  }, [currentDate]);

  // Загрузка отгулов
  const loadDaysOffData = useCallback(async () => {
    try {
      const response = await getAllDaysOff();
      const daysOffMap = {};
      response.days_off.forEach((dayOff) => {
        const dateString = dayOff.date_of_use;
        if (!daysOffMap[dateString]) daysOffMap[dateString] = [];
        daysOffMap[dateString].push({
          user: `${dayOff.surname} ${dayOff.name} ${dayOff.patronymic}`,
          time: dayOff.time,
        });
      });
      setDaysOffData(daysOffMap);
    } catch (error) {
      console.error("Ошибка при загрузке отгулов:", error);
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
    loadVacationData();
    loadBirthdayData();
    loadDaysOffData();
  }, [loadCalendarData, loadVacationData, loadBirthdayData, loadDaysOffData]);

  // Годовой календарь: загрузка данных за год
  useEffect(() => {
    if (selectedView === "year") {
      setYearLoading(true);
      const year = currentDate.getFullYear();
      const dateFrom = `${year}-01-01`;
      const dateTo = `${year}-12-31`;
      getCalendar(dateFrom, dateTo)
        .then((data) => {
          setYearCalendarData(data);
        })
        .finally(() => setYearLoading(false));
    }
  }, [selectedView, currentDate]);

  // Получаем список дней текущего месяца
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;
    // Дни предыдущего месяца
    const prevMonthLastDay = new Date(year, month, 0);
    const daysFromPrevMonth = firstDayOfWeek - 1;
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const prevDate = new Date(
        year,
        month - 1,
        prevMonthLastDay.getDate() - i
      );
      days.push({ date: prevDate, isOtherMonth: true });
    }
    // Дни текущего месяца
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const currentDate = new Date(year, month, i);
      days.push({ date: currentDate, isOtherMonth: false });
    }
    // Дни следующего месяца
    const lastDayOfWeek = days.length % 7 || 7;
    const remainingDays = lastDayOfWeek < 7 ? 7 - lastDayOfWeek : 0;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({ date: nextDate, isOtherMonth: true });
    }
    return days;
  };

  const days = getDaysInMonth(currentDate);

  const handleToggle = (type) => {
    setEnabledTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleResetToggles = () => {
    setEnabledTypes({
      holidays: false,
      corporate: false,
      absences: false,
      birthdays: false,
    });
  };

  const startDate = new Date(
    Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), 1)
  );
  const endDate = new Date(
    Date.UTC(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
  );

  // Функции для перелистывания месяцев
  const handlePrevMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };
  const handleNextMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Для годового календаря: разбиваем данные по месяцам с учётом смещения по дням недели
  const getYearMonths = () => {
    const monthsArr = Array.from({ length: 12 }, (_, i) => []);
    yearCalendarData.forEach((day) => {
      const date = new Date(day.date);
      const month = date.getMonth();
      monthsArr[month].push({
        ...day,
        dateObj: date,
      });
    });
    // Добавляем смещение и пустые ячейки
    return monthsArr.map((monthDays, mIdx) => {
      if (monthDays.length === 0) return [];
      // Сортируем по дате!
      monthDays.sort((a, b) => a.dateObj - b.dateObj);
      const firstDay = monthDays[0].dateObj;
      let firstDayOfWeek = firstDay.getDay();
      firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek; // 1 - Пн, 7 - Вс
      const leadingNulls = Array(firstDayOfWeek - 1).fill(null);
      const lastDay = monthDays[monthDays.length - 1].dateObj;
      let lastDayOfWeek = lastDay.getDay();
      lastDayOfWeek = lastDayOfWeek === 0 ? 7 : lastDayOfWeek;
      const trailingNulls = Array(7 - lastDayOfWeek).fill(null);
      return [...leadingNulls, ...monthDays, ...trailingNulls];
    });
  };

  // Для выбора года в годовом виде
  const handlePrevYear = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setFullYear(newDate.getFullYear() - 1);
      return newDate;
    });
  };
  const handleNextYear = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setFullYear(newDate.getFullYear() + 1);
      return newDate;
    });
  };

  // Для недели:
  const weekRange = getWeekRange(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekRange.start);
    d.setDate(d.getDate() + i);
    return d;
  });

  const handlePrevWeek = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };
  const handleNextWeek = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  // Для дня:
  const handleDayChange = (date) => {
    setCurrentDate(date);
    setSelectedDay(date);
  };

  // Обработчик для редиректа на график отпусков
  const handleVacationScheduleClick = () => {
    navigate("/vacation-schedule");
  };

  const renderDayContent = (day) => {
    const dateStr = formatDateToString(day);
    return (
      <>
        {/* Праздники и мероприятия */}
        {enabledTypes.holidays &&
          calendarData[dateStr] &&
          calendarData[dateStr].event_name && (
            <CustomTooltip
              headerColor="#FFEBEB"
              content={{
                header: (
                  <>
                    <span style={{ fontWeight: 700, color: "#E74C3C" }}>
                      {calendarData[dateStr].event_name}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "#E74C3C",
                        fontWeight: 700,
                      }}
                    >
                      Праздник
                    </span>
                  </>
                ),
                body: (
                  <>
                    <div style={{ color: "#888", fontSize: 14 }}>
                      {day.getDate()} {months[day.getMonth()]}
                    </div>
                  </>
                ),
              }}
            >
              <div className="main-calendar-v2__holiday-block">
                <div className="main-calendar-v2__holiday-content">
                  <div className="main-calendar-v2__holiday-title">
                    {calendarData[dateStr].event_name}
                    <span className="main-calendar-v2__holiday-icon-right">
                      <HolidayIcon
                        style={{ color: "#FF5A5A", width: 18, height: 18 }}
                      />
                    </span>
                  </div>
                </div>
              </div>
            </CustomTooltip>
          )}
        {/* Корпоративные мероприятия */}
        {enabledTypes.corporate &&
          calendarData[dateStr] &&
          calendarData[dateStr].event_type === "corporate" && (
            <CustomTooltip
              headerColor="#C7F5C7"
              content={{
                header: (
                  <>
                    <span style={{ fontWeight: 700, color: "#2A5E38" }}>
                      {calendarData[dateStr].event_name}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "#2A5E38",
                        fontWeight: 700,
                      }}
                    >
                      Корпоратив
                    </span>
                  </>
                ),
                body: (
                  <>
                    <div style={{ color: "#888", fontSize: 14 }}>
                      {day.getDate()} {months[day.getMonth()]}
                    </div>
                  </>
                ),
              }}
            >
              <div
                className="main-calendar-v2__event"
                style={{ background: EVENT_TYPES[1].color }}
              >
                <CorporateIcon
                  style={{
                    color: "#2A5E38",
                    width: 16,
                    height: 16,
                    marginRight: 4,
                    verticalAlign: "middle",
                  }}
                />
                {calendarData[dateStr].event_name}
              </div>
            </CustomTooltip>
          )}
        {/* Отсутствие сотрудников */}
        {enabledTypes.absences && (
          <>
            {(vacationData[dateStr] || []).map((v, i) => {
              let period = "";
              if (v.period_start && v.period_end) {
                const start = new Date(v.period_start);
                const end = new Date(v.period_end);
                const format = (d) =>
                  d.toLocaleDateString("ru-RU", {
                    day: "numeric",
                    month: "long",
                  });
                if (start.getTime() === end.getTime()) {
                  period = format(start);
                } else {
                  period = `${format(start)} - ${format(end)}`;
                }
              }
              return (
                <CustomTooltip
                  key={i}
                  headerColor={absenceColors[v.type] || "#F5F5F5"}
                  content={{
                    header: (
                      <>
                        <span style={{ fontWeight: 700, color: "#1A3A5D" }}>
                          {v.user}
                        </span>
                        <span
                          style={{
                            marginLeft: "auto",
                            color: "#2563A6",
                            fontWeight: 700,
                          }}
                        >
                          {v.type}
                        </span>
                      </>
                    ),
                    body: (
                      <>
                        {v.department && (
                          <div style={{ marginBottom: 2 }}>
                            <span style={{ color: "#8CA1B6" }}>Отдел: </span>
                            <span style={{ color: "#222" }}>
                              {v.department}
                            </span>
                          </div>
                        )}
                        {period && (
                          <div
                            style={{
                              color: "#888",
                              fontSize: 14,
                              marginBottom: 2,
                            }}
                          >
                            {period}
                          </div>
                        )}
                        {v.type === "Командировка" && v.project && (
                          <div style={{ marginBottom: 2 }}>
                            <span style={{ color: "#8CA1B6" }}>Проект: </span>
                            <span style={{ color: "#222" }}>{v.project}</span>
                          </div>
                        )}
                      </>
                    ),
                  }}
                >
                  <div
                    className="main-calendar-v2__absence"
                    style={{
                      background: absenceColors[v.type] || "#F5F5F5",
                      borderRadius: 6,
                      marginBottom: 2,
                      fontSize: 13,
                      color: "#222",
                      display: "flex",
                      alignItems: "center",
                      minHeight: 20,
                      cursor: "pointer",
                      justifyContent: "center",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {shortName(v.user)}
                  </div>
                </CustomTooltip>
              );
            })}
            {(daysOffData[dateStr] || []).map((d, i) => {
              let timeStr = d.time;
              if (
                typeof d.time === "number" ||
                (typeof d.time === "string" && /^\d+$/.test(d.time))
              ) {
                const sec = Number(d.time);
                const h = Math.floor(sec / 3600);
                const m = Math.floor((sec % 3600) / 60);
                timeStr = h > 0 ? `${h} ч.` : "";
                if (m > 0) timeStr += ` ${m} мин.`;
                if (!timeStr) timeStr = "1 ч.";
              }
              return (
                <CustomTooltip
                  key={i}
                  headerColor="#EAF2FF"
                  content={{
                    header: (
                      <>
                        <span style={{ fontWeight: 700, color: "#1A3A5D" }}>
                          {d.user}
                        </span>
                        <span
                          style={{
                            marginLeft: "auto",
                            color: "#2563A6",
                            fontWeight: 700,
                          }}
                        >
                          Отгул
                        </span>
                      </>
                    ),
                    body: (
                      <>
                        <div
                          style={{
                            color: "#888",
                            fontSize: 14,
                            marginBottom: 2,
                          }}
                        >
                          {day.getDate()} {months[day.getMonth()]} ({timeStr})
                        </div>
                        {d.department && (
                          <div style={{ marginBottom: 2 }}>
                            <span style={{ color: "#8CA1B6" }}>Отдел: </span>
                            <span style={{ color: "#222" }}>
                              {d.department}
                            </span>
                          </div>
                        )}
                        {d.reason && (
                          <div>
                            <span style={{ color: "#8CA1B6" }}>Причина: </span>
                            <span style={{ color: "#222" }}>{d.reason}</span>
                          </div>
                        )}
                      </>
                    ),
                  }}
                >
                  <div
                    className="main-calendar-v2__absence"
                    style={{
                      background: absenceColors["Отгул"],
                      borderRadius: 6,
                      marginBottom: 2,
                      fontSize: 13,
                      color: "#222",
                      display: "flex",
                      alignItems: "center",
                      minHeight: 20,
                      cursor: "pointer",
                      justifyContent: "center",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {shortName(d.user)}
                  </div>
                </CustomTooltip>
              );
            })}
          </>
        )}
        {/* Дни рождения */}
        {enabledTypes.birthdays &&
          birthdayData[dateStr] &&
          birthdayData[dateStr].map((b, i) => (
            <CustomTooltip
              key={i}
              headerColor={b.isJubilee ? "rgba(255, 215, 0, 0.1)" : "#E3F1FF"}
              content={{
                header: (
                  <>
                    <span style={{ fontWeight: 700, color: "#1A3A5D" }}>
                      {b.user}
                    </span>
                    <span
                      style={{
                        marginLeft: "auto",
                        color: "#3A8DFF",
                        fontWeight: 700,
                      }}
                    >
                      День рождения
                    </span>
                  </>
                ),
                body: (
                  <>
                    <div style={{ color: "#888", fontSize: 14 }}>
                      {day.getDate()} {months[day.getMonth()]}
                    </div>
                  </>
                ),
              }}
            >
              <div
                className={`main-calendar-v2__birthday-block ${
                  b.isJubilee ? "main-calendar-v2__birthday-block--jubilee" : ""
                }`}
              >
                <div className="main-calendar-v2__birthday-content">
                  <div className="main-calendar-v2__birthday-name">
                    {b.user}
                  </div>
                  <span className="main-calendar-v2__birthday-icon-right">
                    <CakeIcon
                      style={{
                        color: b.isJubilee ? "#FFD700" : "#3A8DFF",
                        width: 18,
                        height: 18,
                      }}
                    />
                  </span>
                </div>
              </div>
            </CustomTooltip>
          ))}
      </>
    );
  };

  return (
    <div className="main-calendar-v2">
      <div className="main-calendar-v2__sidebar">
        <div className="main-calendar-v2__toggles">
          <div className="calendar-toggles__header">
            <span className="calendar-toggles__title">СОБЫТИЯ</span>
            <span
              className="calendar-toggles__reset"
              onClick={selectedView === "year" ? undefined : handleResetToggles}
              style={{
                color: selectedView === "year" ? "#ccc" : undefined,
                cursor: selectedView === "year" ? "not-allowed" : undefined,
              }}
            >
              Сбросить Все
            </span>
          </div>
          {EVENT_TYPES.map((type) => (
            <div key={type.id} className="calendar-toggle">
              <div
                className={`calendar-toggle__icon calendar-toggle__icon--${type.id}`}
                style={{
                  background: ICON_BG[type.id],
                  opacity: selectedView === "year" ? 0.5 : 1,
                }}
              >
                {ICONS[type.id]}
              </div>
              <span
                className="calendar-toggle__label"
                style={{ color: selectedView === "year" ? "#bbb" : undefined }}
              >
                {type.label}
              </span>
              <label className="calendar-toggle__switch-label">
                <input
                  type="checkbox"
                  checked={
                    selectedView === "year" ? false : enabledTypes[type.id]
                  }
                  onChange={
                    selectedView === "year"
                      ? undefined
                      : () => handleToggle(type.id)
                  }
                  disabled={selectedView === "year"}
                  className="calendar-toggle__switch-input"
                />
                <span
                  className="calendar-toggle__switch-slider"
                  style={{
                    background: selectedView === "year" ? "#eee" : undefined,
                  }}
                ></span>
              </label>
            </div>
          ))}
        </div>
        {/* Мини-календарь месяца под тумблерами (можно скрыть в годовом виде или оставить) */}
        {selectedView !== "year" && (
          <div className="main-calendar-v2__mini-month">
            <div className="main-calendar-v2__mini-month-title">
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            <div className="main-calendar-v2__mini-month-grid">
              {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((d) => (
                <div key={d} className="main-calendar-v2__mini-month-weekday">
                  {d}
                </div>
              ))}
              {days.map((day, idx) => {
                const dateStr = formatDateToString(day.date);
                const isToday = dateStr === formatDateToString(new Date());
                const calendarDay = calendarData[dateStr];
                const isHoliday =
                  calendarDay && calendarDay.workday_flag === false;
                const isWeekend = [6, 0].includes(day.date.getDay());
                return (
                  <div
                    key={idx}
                    className={`main-calendar-v2__mini-month-day${
                      day.isOtherMonth ? " other-month" : ""
                    }${isToday ? " today" : ""}${
                      isHoliday
                        ? " holiday"
                        : !isHoliday && isWeekend
                        ? " weekend"
                        : ""
                    }`}
                    style={{
                      background: isToday ? "#2A5E38" : undefined,
                      color: isToday ? "#fff" : undefined,
                      fontWeight: isToday ? 700 : undefined,
                    }}
                  >
                    {day.date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Кнопка "График отпусков" */}
        {selectedView !== "year" && (
          <div className="main-calendar-v2__vacation-schedule-btn-container">
            <button
              className="main-calendar-v2__vacation-schedule-btn"
              onClick={handleVacationScheduleClick}
            >
              График отпусков
            </button>
          </div>
        )}
      </div>
      <div className="main-calendar-v2__content">
        <div className="main-calendar-v2__view-selector">
          {[
            { id: "year", label: "Год" },
            { id: "month", label: "Месяц" },
            { id: "week", label: "Неделя" },
            { id: "day", label: "День" },
          ].map((view) => (
            <button
              key={view.id}
              className={selectedView === view.id ? "active" : ""}
              onClick={() => setSelectedView(view.id)}
            >
              {view.label}
            </button>
          ))}
        </div>
        {/* Навигация по месяцам (только для month view, по центру) */}
        {selectedView === "month" && (
          <div
            className="main-calendar-v2__month-navigation"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <button
              className="main-calendar-v2__nav-btn"
              onClick={handlePrevMonth}
              style={{
                fontSize: 22,
                background: "none",
                border: "none",
                color: "#2A5E38",
                cursor: "pointer",
                borderRadius: 6,
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Предыдущий месяц"
            >
              ←
            </button>
            <span
              style={{
                fontWeight: 600,
                fontSize: 18,
                color: "#2A5E38",
                minWidth: 140,
                textAlign: "center",
              }}
            >
              {months[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <button
              className="main-calendar-v2__nav-btn"
              onClick={handleNextMonth}
              style={{
                fontSize: 22,
                background: "none",
                border: "none",
                color: "#2A5E38",
                cursor: "pointer",
                borderRadius: 6,
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="Следующий месяц"
            >
              →
            </button>
          </div>
        )}
        {/* Годовой календарь */}
        {selectedView === "year" && (
          <div style={{ margin: "24px 0" }}>
            {/* Выбор года */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <button
                className="main-calendar-v2__nav-btn"
                onClick={handlePrevYear}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Предыдущий год"
              >
                ←
              </button>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 20,
                  color: "#2A5E38",
                  minWidth: 100,
                  textAlign: "center",
                }}
              >
                {currentDate.getFullYear()}
              </span>
              <button
                className="main-calendar-v2__nav-btn"
                onClick={handleNextYear}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Следующий год"
              >
                →
              </button>
            </div>
            {yearLoading ? (
              <div style={{ textAlign: "center", color: "#888", fontSize: 18 }}>
                Загрузка календаря за год...
              </div>
            ) : (
              <div
                className="main-calendar-v2__year-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 24,
                }}
              >
                {getYearMonths().map((monthDays, mIdx) => (
                  <div
                    key={mIdx}
                    className="main-calendar-v2__year-month"
                    style={{
                      background: "#F8F9FA",
                      borderRadius: 10,
                      padding: 12,
                      border:
                        mIdx === new Date().getMonth()
                          ? "2px solid #2A5E38"
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        textAlign: "center",
                        fontWeight: 600,
                        marginBottom: 6,
                        color:
                          mIdx === new Date().getMonth() ? "#2A5E38" : "#666",
                      }}
                    >
                      {months[mIdx]}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        gap: 2,
                      }}
                    >
                      {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((d) => (
                        <div
                          key={d}
                          style={{
                            textAlign: "center",
                            fontSize: 12,
                            color: "#888",
                            fontWeight: 500,
                          }}
                        >
                          {d}
                        </div>
                      ))}
                      {monthDays.map((day, idx) => {
                        if (!day) {
                          return <div key={idx}></div>;
                        }
                        const isWeekend = [6, 0].includes(day.dateObj.getDay());
                        const isHoliday = day.workday_flag === false;
                        const isToday =
                          formatDateToString(day.dateObj) ===
                          formatDateToString(new Date());
                        // Если есть event_name (праздник), показываем тултип
                        const dayContent = day.event_name ? (
                          <CustomTooltip
                            headerColor="#FFEBEB"
                            content={{
                              header: (
                                <span
                                  style={{ fontWeight: 700, color: "#E74C3C" }}
                                >
                                  {day.event_name}
                                </span>
                              ),
                              body: (
                                <span style={{ color: "#888", fontSize: 13 }}>
                                  {day.dateObj.getDate()}{" "}
                                  {months[day.dateObj.getMonth()]}
                                </span>
                              ),
                            }}
                          >
                            <span>{day.dateObj.getDate()}</span>
                          </CustomTooltip>
                        ) : (
                          day.dateObj.getDate()
                        );
                        return (
                          <div
                            key={idx}
                            style={{
                              textAlign: "center",
                              fontSize: 13,
                              borderRadius: 4,
                              padding: "2px 0",
                              background: isToday
                                ? "#2A5E38"
                                : isHoliday
                                ? "#FFEBEB"
                                : isWeekend
                                ? "#FFF6ED"
                                : "#fff",
                              color: isToday
                                ? "#fff"
                                : isHoliday
                                ? "#E74C3C"
                                : isWeekend
                                ? "#E08A2B"
                                : "#222",
                              opacity: 1,
                              fontWeight: isHoliday || isToday ? 700 : 500,
                              cursor: day.event_name ? "pointer" : undefined,
                            }}
                          >
                            {dayContent}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Логика отображения разных видов календаря */}
        {selectedView === "month" && (
          <>
            <div className="main-calendar-v2__grid">
              <div className="main-calendar-v2__weekdays">
                <div>Пн</div>
                <div>Вт</div>
                <div>Ср</div>
                <div>Чт</div>
                <div>Пт</div>
                <div>Сб</div>
                <div>Вс</div>
              </div>
              <div className="main-calendar-v2__days">
                {days.map((day, idx) => {
                  const dateStr = formatDateToString(day.date);
                  const isWeekendOrHoliday =
                    [6, 0].includes(day.date.getDay()) ||
                    (calendarData[dateStr] &&
                      calendarData[dateStr].workday_flag === false);
                  const isToday =
                    formatDateToString(day.date) ===
                    formatDateToString(new Date());
                  return (
                    <div
                      key={idx}
                      className={`main-calendar-v2__day${
                        day.isOtherMonth ? " other-month" : ""
                      }${isWeekendOrHoliday ? " weekend" : ""}${
                        isToday ? " today" : ""
                      }`}
                    >
                      <div className="main-calendar-v2__day-number">
                        {day.date.getDate()}
                      </div>
                      <div className="main-calendar-v2__day-content">
                        {renderDayContent(day.date)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Списки под календарём */}
            <div className="main-calendar-v2__lists">
              {enabledTypes.holidays && (
                <EventsList dateRange={{ startDate, endDate }} />
              )}
              {enabledTypes.absences && (
                <VacationList
                  vacations={Object.values(vacationData).flat()}
                  daysOff={Object.entries(daysOffData).flatMap(([date, arr]) =>
                    arr.map((d) => ({ ...d, date_of_use: date }))
                  )}
                  dateRange={{ startDate, endDate }}
                />
              )}
              {enabledTypes.birthdays && (
                <BirthdayList dateRange={{ startDate, endDate }} />
              )}
            </div>
          </>
        )}
        {selectedView === "week" && (
          <div style={{ margin: "24px 0" }}>
            {/* Навигация по неделям */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <button
                className="main-calendar-v2__nav-btn"
                onClick={handlePrevWeek}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Предыдущая неделя"
              >
                ←
              </button>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#2A5E38",
                  minWidth: 180,
                  textAlign: "center",
                }}
              >
                {weekRange.start.toLocaleDateString("ru-RU", {
                  day: "numeric",
                  month: "long",
                })}{" "}
                -{" "}
                {weekRange.end.toLocaleDateString("ru-RU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                className="main-calendar-v2__nav-btn"
                onClick={handleNextWeek}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Следующая неделя"
              >
                →
              </button>
            </div>
            <div className="main-calendar-v2__grid">
              <div className="main-calendar-v2__weekdays">
                {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="main-calendar-v2__days">
                {weekDays.map((day, idx) => {
                  const dateStr = formatDateToString(day);
                  const isWeekendOrHoliday =
                    [6, 0].includes(day.getDay()) ||
                    (calendarData[dateStr] &&
                      calendarData[dateStr].workday_flag === false);
                  const isToday =
                    formatDateToString(day) === formatDateToString(new Date());
                  return (
                    <div
                      key={idx}
                      className={`main-calendar-v2__day${
                        isWeekendOrHoliday ? " weekend" : ""
                      }${isToday ? " today" : ""}`}
                      style={{
                        minHeight: "unset",
                        maxHeight: "unset",
                        height: "auto",
                      }}
                    >
                      <div className="main-calendar-v2__day-number">
                        {day.getDate()}
                      </div>
                      <div className="main-calendar-v2__day-content">
                        {renderDayContent(day)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="main-calendar-v2__lists">
              {enabledTypes.holidays && (
                <EventsList
                  dateRange={{
                    startDate: weekRange.start,
                    endDate: weekRange.end,
                  }}
                />
              )}
              {enabledTypes.absences && (
                <VacationList
                  vacations={Object.values(vacationData).flat()}
                  daysOff={Object.entries(daysOffData).flatMap(([date, arr]) =>
                    arr.map((d) => ({ ...d, date_of_use: date }))
                  )}
                  dateRange={{
                    startDate: weekRange.start,
                    endDate: weekRange.end,
                  }}
                />
              )}
              {enabledTypes.birthdays && (
                <BirthdayList
                  dateRange={{
                    startDate: weekRange.start,
                    endDate: weekRange.end,
                  }}
                />
              )}
            </div>
          </div>
        )}
        {selectedView === "day" && (
          <div style={{ margin: "24px 0" }}>
            {/* Навигация по дням */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <button
                className="main-calendar-v2__nav-btn"
                onClick={() => {
                  const newDate = new Date(selectedDay);
                  newDate.setDate(newDate.getDate() - 1);
                  handleDayChange(newDate);
                }}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Предыдущий день"
              >
                ←
              </button>
              <div style={{ position: "relative" }}>
                <DatePicker
                  selected={selectedDay}
                  onChange={handleDayChange}
                  dateFormat="dd MMMM yyyy"
                  locale={ru}
                  customInput={
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 18,
                        color: "#2A5E38",
                        minWidth: 180,
                        textAlign: "center",
                        cursor: "pointer",
                        display: "block",
                      }}
                    >
                      {selectedDay.toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  }
                  popperPlacement="bottom"
                  popperClassName="main-calendar-v2__datepicker-popper"
                  renderDayContents={(day, date) => {
                    // day — это число (день месяца), date — объект Date (если есть)
                    let realDate;
                    if (date instanceof Date && !isNaN(date)) {
                      realDate = date;
                    } else {
                      // Fallback: вычисляем дату на основе selectedDay
                      realDate = new Date(
                        selectedDay.getFullYear(),
                        selectedDay.getMonth(),
                        day
                      );
                    }
                    const dateStr = formatDateToString(realDate);
                    const isWeekend = [6, 0].includes(realDate.getDay());
                    const isHoliday =
                      calendarData[dateStr] &&
                      calendarData[dateStr].workday_flag === false;
                    const isToday = dateStr === formatDateToString(new Date());
                    return (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isToday
                            ? "#2A5E38"
                            : isHoliday
                            ? "#FFEBEB"
                            : isWeekend
                            ? "#FFF6ED"
                            : "transparent",
                          color: isToday
                            ? "#fff"
                            : isHoliday
                            ? "#E74C3C"
                            : isWeekend
                            ? "#E08A2B"
                            : "#222",
                          fontWeight: isHoliday || isToday ? 700 : 500,
                          borderRadius: 4,
                        }}
                      >
                        {day}
                      </div>
                    );
                  }}
                />
              </div>
              <button
                className="main-calendar-v2__nav-btn"
                onClick={() => {
                  const newDate = new Date(selectedDay);
                  newDate.setDate(newDate.getDate() + 1);
                  handleDayChange(newDate);
                }}
                style={{
                  fontSize: 22,
                  background: "none",
                  border: "none",
                  color: "#2A5E38",
                  cursor: "pointer",
                  borderRadius: 6,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-label="Следующий день"
              >
                →
              </button>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  minWidth: 400,
                  maxWidth: 600,
                  width: "100%",
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  padding: 32,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    marginBottom: 24,
                    color: "#2A5E38",
                  }}
                >
                  {selectedDay
                    .toLocaleDateString("ru-RU", { weekday: "long" })
                    .replace(/^./, (str) => str.toUpperCase())}
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {renderDayContent(selectedDay)}
                </div>
              </div>
            </div>
            <div className="main-calendar-v2__lists">
              {enabledTypes.holidays && (
                <EventsList
                  dateRange={{ startDate: selectedDay, endDate: selectedDay }}
                />
              )}
              {enabledTypes.absences && (
                <VacationList
                  vacations={Object.values(vacationData).flat()}
                  daysOff={Object.entries(daysOffData).flatMap(([date, arr]) =>
                    arr.map((d) => ({ ...d, date_of_use: date }))
                  )}
                  dateRange={{ startDate: selectedDay, endDate: selectedDay }}
                />
              )}
              {enabledTypes.birthdays && (
                <BirthdayList
                  dateRange={{ startDate: selectedDay, endDate: selectedDay }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainCalendarV2;
